/**
 * <product-card> Custom Element
 *
 * Card-level concerns only:
 *   - Quick-add hover (desktop only) reveals the variant picker.
 *   - On `variant:change` from the child <add-item-form>, sync the
 *     `.js-product-link` href to point at the selected variant and
 *     toggle the form/hover-target visibility.
 *   - Quick-add ATC-button click hides the quick-add wrapper.
 *
 * Variant resolution, form state, fast-quick-add submit, and the
 * radio-reclick deselect all live on <add-item-form>. This element
 * is a passive listener over native CustomEvents — no jQuery, no
 * delegation tricks, no microtask deferrals.
 *
 * Source path: theme/js/components/product-card.esm.js
 * Built name : ./product-card.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-06
 * Model: Claude Opus 4.7
 *
 * [ProductCard]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';

const QUICK_ADD_SELECTOR = '.js-quick-add-to-cart';
const HOVER_TARGET_SELECTOR = '.js-hover-target';
const FORM_WRAP_SELECTOR = '.js-form-wrap';
const PRODUCT_LINK_SELECTOR = '.js-product-link';
const CARD_INNER_SELECTOR = '.js-card-inner';
const ADD_ITEM_FORM_SELECTOR = 'add-item-form';

function isVisible(el) {
  return !!el && el.offsetParent !== null;
}

class ProductCard extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    if (this.classList.contains('grid-tout')) return;

    const cardInner = this.querySelector(CARD_INNER_SELECTOR);

    this.addEventListener('click', this._onClick);
    this.addEventListener('variant:change', this._onVariantChange);

    if (cardInner && this.classList.contains('can-quick-add')) {
      cardInner.addEventListener('mouseenter', this._onCardEnter);
      cardInner.addEventListener('mouseleave', this._onCardLeave);
    }

    const aif = this.querySelector(ADD_ITEM_FORM_SELECTOR);
    const form = aif ? aif.querySelector('form') : null;
    if (form && form.classList.contains('has-selected-variant')) {
      this._updateProductLinks(form);
    }
  }

  disconnectedCallback() {
    if (this._quickAdd) {
      this._quickAdd.removeEventListener('mouseenter', this._onQuickAddEnter);
      this._quickAdd.removeEventListener('mouseleave', this._onQuickAddLeave);
      this._quickAdd = null;
    }
  }

  _onClick = (event) => {
    if (event.target.closest(`${QUICK_ADD_SELECTOR} form button`)) {
      const quickAdd = this.querySelector(QUICK_ADD_SELECTOR);
      if (quickAdd) quickAdd.style.display = 'none';
    }
  };

  _onVariantChange = (event) => {
    const form = event.detail?.form;
    if (!form) return;
    const quickAdd = this.querySelector(QUICK_ADD_SELECTOR);
    if (!quickAdd) return;
    const hoverTarget = quickAdd.querySelector(HOVER_TARGET_SELECTOR);
    const formWrap = quickAdd.querySelector(FORM_WRAP_SELECTOR);
    if (!formWrap) return;

    const formVisible = isVisible(formWrap);
    const hasVariant = form.classList.contains('has-selected-variant');

    this._updateProductLinks(form);

    if (!formVisible && hasVariant) {
      if (hoverTarget) hoverTarget.style.display = 'none';
      formWrap.style.display = 'block';
    } else if (!hasVariant) {
      if (hoverTarget) hoverTarget.style.display = '';
      formWrap.style.display = 'none';
    }
  };

  _onCardEnter = () => {
    const breakpoint = appGet('breakpoints.smallMedium');
    if (breakpoint && breakpoint.matches) return;
    const quickAdd = this.querySelector(QUICK_ADD_SELECTOR);
    if (!quickAdd) return;
    quickAdd.style.display = 'block';
    this._quickAdd = quickAdd;
    quickAdd.addEventListener('mouseenter', this._onQuickAddEnter);
    quickAdd.addEventListener('mouseleave', this._onQuickAddLeave);
  };

  _onCardLeave = () => {
    const breakpoint = appGet('breakpoints.smallMedium');
    if (breakpoint && breakpoint.matches) return;
    const quickAdd = this._quickAdd || this.querySelector(QUICK_ADD_SELECTOR);
    if (!quickAdd) return;
    quickAdd.style.display = 'none';
    quickAdd.removeEventListener('mouseenter', this._onQuickAddEnter);
    quickAdd.removeEventListener('mouseleave', this._onQuickAddLeave);
    this._quickAdd = null;
  };

  _onQuickAddEnter = () => {
    const quickAdd = this._quickAdd;
    if (!quickAdd) return;
    const hoverTarget = quickAdd.querySelector(HOVER_TARGET_SELECTOR);
    const formWrap = quickAdd.querySelector(FORM_WRAP_SELECTOR);
    if (!formWrap) return;
    if (isVisible(formWrap)) return;
    if (hoverTarget) hoverTarget.style.display = 'none';
    formWrap.style.display = 'block';
  };

  _onQuickAddLeave = () => {
    const quickAdd = this._quickAdd;
    if (!quickAdd) return;
    const hoverTarget = quickAdd.querySelector(HOVER_TARGET_SELECTOR);
    const formWrap = quickAdd.querySelector(FORM_WRAP_SELECTOR);
    if (!formWrap) return;
    const innerForm = formWrap.querySelector('form');
    const keepFormVisible = !!innerForm && innerForm.classList.contains('has-selected-variant');
    if (isVisible(formWrap) && !keepFormVisible) {
      if (hoverTarget) hoverTarget.style.display = '';
      formWrap.style.display = 'none';
    }
  };

  _updateProductLinks(form) {
    const variantId = form.classList.contains('has-selected-variant')
      ? form.querySelector('input[name="id"]')?.value
      : null;
    if (form.classList.contains('has-selected-variant') && !variantId) return;

    this.querySelectorAll(PRODUCT_LINK_SELECTOR).forEach((link) => {
      const currentHref = link.getAttribute('href') || '';
      const baseUrl = currentHref.split('?')[0];
      const newHref = variantId ? `${baseUrl}?variant=${variantId}` : baseUrl;
      if (link.getAttribute('href') !== newHref) {
        link.setAttribute('href', newHref);
      }
    });
  }
}

customElements.define('product-card', ProductCard);
log('[ProductCard] registered');

export { ProductCard };
